"use client";
"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Slider = void 0;
const react_1 = __importDefault(require("react"));
const foundations_1 = require("../../foundations");
const Text_1 = require("../../foundations/Typography/Text");
const useId_1 = require("../../hooks/useId");
const helpers_1 = require("./helpers");
const SliderHandle_1 = require("./SliderHandle");
const SliderMarker_1 = require("./SliderMarker");
const SliderTrack_1 = require("./SliderTrack");
const Slider = ({ disabled = false, disableSwap = false, label, marks = false, max = 100, min = 0, onChange, step = 1, value, variant = "continuous" }) => {
    const slider = react_1.default.useMemo(() => ({
        disabled,
        max,
        min,
        step: Math.max(0.00001, step),
        value: (0, helpers_1.getSortedValue)(Array.isArray(value) ? value : [min, value]).map((val) => Math.max(Math.min(val, max), min)),
        variant
    }), [disabled, max, min, step, value, variant]);
    const [dragging, draggingSet] = react_1.default.useState(false);
    const fillBackgroundColor = react_1.default.useMemo(() => {
        if (disabled) {
            return "slider-surface-disabled";
        }
        else if (dragging) {
            return "slider-surface-selected-click";
        }
        else {
            return "slider-surface-selected-enabled";
        }
    }, [disabled, dragging]);
    const center = min + (max - min) / 2;
    const dragDirection = react_1.default.useRef("end");
    const dragPosStart = react_1.default.useRef(0);
    const fillClassName = `slider${(0, useId_1.useId)().replace(/^[A-Z0-9]gi/, "")}`;
    const labelColor = disabled
        ? "input-text-disabled"
        : "input-text";
    const labelId = `slider${(0, useId_1.useId)().replace(/^[A-Z0-9]gi/, "")}`;
    const labelStyles = {
        display: "flex",
        fontSize: "font-size-4",
        lineHeight: "leading-2"
    };
    const stepArray = new Array(Math.round((max - min) / slider.step) + 1).fill(null);
    const stepDecimalPlaces = (0, helpers_1.getDecimalPlaces)(slider.step);
    const trackRef = react_1.default.useRef(null);
    const handleMove = react_1.default.useCallback((clientX) => {
        if (!trackRef.current) {
            return;
        }
        const { left, width } = trackRef.current.getBoundingClientRect();
        const pointerXChange = clientX - dragPosStart.current;
        const percentX = (dragPosStart.current + pointerXChange - left) / width;
        if (slider.variant === "range") {
            if (dragDirection.current === "end") {
                const valueNext = (0, helpers_1.getSliderValueFromPercent)(percentX, slider.max, disableSwap ? slider.value[0] : slider.min, slider);
                onChange((0, helpers_1.getSortedValue)([slider.value[0], valueNext]));
            }
            else {
                const valueNext = (0, helpers_1.getSliderValueFromPercent)(percentX, disableSwap ? slider.value[1] : slider.max, slider.min, slider);
                onChange((0, helpers_1.getSortedValue)([valueNext, slider.value[1]]));
            }
        }
        else {
            const valueNext = (0, helpers_1.getSliderValueFromPercent)(percentX, slider.max, slider.min, slider);
            onChange(valueNext);
        }
    }, [disableSwap, onChange, slider]);
    const handleWindowMouseMove = react_1.default.useCallback((e) => handleMove(e.clientX), [handleMove]);
    const handleWindowTouchMove = react_1.default.useCallback((e) => {
        if (e.touches[0]) {
            e.preventDefault();
            handleMove(e.touches[0].clientX);
        }
    }, [handleMove]);
    const handleKeyDown = react_1.default.useCallback((e) => {
        const valueIndex = slider.variant === "range" && dragDirection.current === "start" ? 0 : 1;
        let valueNext = null;
        switch (e.key) {
            case "ArrowRight":
            case "ArrowUp":
                valueNext = (0, helpers_1.getSliderValue)(slider.value[valueIndex] + slider.step, slider.variant === "range" &&
                    dragDirection.current === "start" &&
                    disableSwap
                    ? slider.value[1]
                    : slider.max, slider.min, slider);
                break;
            case "ArrowLeft":
            case "ArrowDown":
                valueNext = (0, helpers_1.getSliderValue)(slider.value[valueIndex] - slider.step, slider.max, slider.variant === "range" &&
                    dragDirection.current === "end" &&
                    disableSwap
                    ? slider.value[0]
                    : slider.min, slider);
                break;
            case "Home":
                valueNext = slider.min;
                break;
            case "End":
                valueNext = slider.max;
                break;
        }
        if (valueNext === null) {
            return;
        }
        if (slider.variant === "range") {
            if (dragDirection.current === "end") {
                onChange((0, helpers_1.getSortedValue)([slider.value[0], valueNext]));
            }
            else {
                onChange((0, helpers_1.getSortedValue)([valueNext, slider.value[1]]));
            }
        }
        else {
            onChange(valueNext);
        }
    }, [disableSwap, onChange, slider]);
    const handlePointerDown = react_1.default.useCallback((e) => {
        if (e.button !== 0 || disabled || !trackRef.current) {
            return;
        }
        const { left, width } = trackRef.current.getBoundingClientRect();
        const percentX = (e.clientX - left) / width;
        const valueNext = (0, helpers_1.getSliderValueFromPercent)(percentX, slider.max, slider.min, slider);
        // Determine which handle/direction to drag based on whichever handle the
        // pointer event was closest to
        dragDirection.current =
            valueNext < slider.value[0] + (slider.value[1] - slider.value[0]) / 2
                ? "start"
                : "end";
        dragPosStart.current = e.clientX;
        const cancelDrag = () => {
            draggingSet(false);
            window.removeEventListener("mousemove", handleWindowMouseMove);
            window.removeEventListener("touchmove", handleWindowTouchMove);
        };
        draggingSet(true);
        handleMove(e.clientX);
        window.addEventListener("mousemove", handleWindowMouseMove);
        window.addEventListener("touchmove", handleWindowTouchMove, {
            passive: false
        });
        window.addEventListener("mouseup", cancelDrag);
        window.addEventListener("touchend", cancelDrag);
    }, [disabled, handleWindowMouseMove, handleWindowTouchMove, handleMove, slider]);
    return (react_1.default.createElement(foundations_1.Box, { sx: {
            color: labelColor,
            display: "flex",
            flexDirection: "column",
            [`.${fillClassName}`]: {
                backgroundColor: fillBackgroundColor,
                transition: "transition-color-medium"
            },
            [`:hover .${fillClassName}`]: disabled
                ? {}
                : {
                    backgroundColor: dragging
                        ? "slider-surface-selected-click"
                        : "slider-surface-selected-hover"
                }
        } },
        variant === "range" && (react_1.default.createElement(foundations_1.Box, { as: "label", id: labelId, sx: Object.assign(Object.assign({}, labelStyles), { flexDirection: "column" }) },
            label && react_1.default.createElement(Text_1.Text, null, label),
            react_1.default.createElement(foundations_1.Box, { sx: { display: "flex", paddingBottom: 1 } },
                react_1.default.createElement(Text_1.Text, null, slider.value[0].toFixed(stepDecimalPlaces)),
                react_1.default.createElement(Text_1.Text, { sx: { marginInlineStart: "auto" } }, slider.value[1].toFixed(stepDecimalPlaces))))),
        variant !== "range" && (react_1.default.createElement(foundations_1.Box, { as: "label", id: labelId, sx: labelStyles },
            label && react_1.default.createElement(Text_1.Text, null, label),
            react_1.default.createElement(Text_1.Text, { sx: { marginInlineStart: "auto" } }, slider.value[1].toFixed(stepDecimalPlaces)))),
        react_1.default.createElement(foundations_1.Box, { sx: { position: "relative" } },
            react_1.default.createElement(foundations_1.Box, { "aria-hidden": true, as: "button", onKeyDown: handleKeyDown, onPointerDown: handlePointerDown, ref: trackRef, sx: {
                    containerType: "size",
                    cursor: disabled ? "not-allowed" : "pointer",
                    height: 8,
                    position: "relative",
                    userSelect: false,
                    width: "100%"
                }, tabIndex: -1 },
                variant === "center-set" && (react_1.default.createElement(react_1.default.Fragment, null,
                    react_1.default.createElement(SliderTrack_1.SliderTrack, { leaveSpaceAtEnd: slider.value[1] <= center ? true : false, roundStart: true, slider: slider, trackEnd: Math.min(slider.value[1], center), trackStart: min }),
                    react_1.default.createElement(SliderTrack_1.SliderTrack, { leaveSpaceAtStart: slider.value[1] >= center ? true : false, roundEnd: true, slider: slider, trackEnd: max, trackStart: Math.max(slider.value[1], center) }),
                    react_1.default.createElement(SliderTrack_1.SliderTrack, { fillClassName: fillClassName, leaveSpaceAtEnd: slider.value[1] >= center ? true : false, leaveSpaceAtStart: slider.value[1] <= center ? true : false, slider: slider, trackEnd: slider.value[1] > center ? slider.value[1] : center, trackStart: slider.value[1] < center ? slider.value[1] : center }))),
                variant === "continuous" && (react_1.default.createElement(react_1.default.Fragment, null,
                    react_1.default.createElement(SliderTrack_1.SliderTrack, { fillClassName: fillClassName, leaveSpaceAtEnd: true, roundStart: true, slider: slider, trackEnd: slider.value[1], trackStart: min }),
                    react_1.default.createElement(SliderTrack_1.SliderTrack, { leaveSpaceAtStart: true, roundEnd: true, slider: slider, trackEnd: max, trackStart: slider.value[1] }))),
                variant === "range" && (react_1.default.createElement(react_1.default.Fragment, null,
                    react_1.default.createElement(SliderTrack_1.SliderTrack, { leaveSpaceAtEnd: true, roundStart: true, slider: slider, trackEnd: slider.value[0], trackStart: min }),
                    react_1.default.createElement(SliderTrack_1.SliderTrack, { fillClassName: fillClassName, leaveSpaceAtEnd: true, leaveSpaceAtStart: true, slider: slider, trackEnd: slider.value[1], trackStart: slider.value[0] }),
                    react_1.default.createElement(SliderTrack_1.SliderTrack, { leaveSpaceAtStart: true, roundEnd: true, slider: slider, trackEnd: max, trackStart: slider.value[1] }))),
                marks && (react_1.default.createElement(foundations_1.Box, { as: "span", sx: {
                        display: "block",
                        left: 0,
                        position: "absolute",
                        top: 0,
                        width: "100%"
                    } }, stepArray.map((_step, index) => (react_1.default.createElement(SliderMarker_1.SliderMarker, { key: index, markerIndex: index, markersTotal: stepArray.length, slider: slider })))))),
            react_1.default.createElement(foundations_1.Box, { sx: {
                    containerType: "size",
                    left: 0,
                    position: "absolute",
                    top: 0,
                    width: "100%"
                } },
                variant === "range" && (react_1.default.createElement(react_1.default.Fragment, null,
                    react_1.default.createElement(SliderHandle_1.SliderHandle, { fillClassName: fillClassName, labelId: labelId, onFocus: () => {
                            dragDirection.current = "start";
                        }, onKeyDown: handleKeyDown, onPointerDown: handlePointerDown, slider: slider, value: slider.value[0] }),
                    react_1.default.createElement(SliderHandle_1.SliderHandle, { fillClassName: fillClassName, labelId: labelId, onFocus: () => {
                            dragDirection.current = "end";
                        }, onKeyDown: handleKeyDown, onPointerDown: handlePointerDown, slider: slider, value: slider.value[1] }))),
                variant !== "range" && (react_1.default.createElement(SliderHandle_1.SliderHandle, { fillClassName: fillClassName, labelId: labelId, onFocus: () => {
                        dragDirection.current = "end";
                    }, onKeyDown: handleKeyDown, onPointerDown: handlePointerDown, slider: slider, value: slider.value[1] }))))));
};
exports.Slider = Slider;
//# sourceMappingURL=Slider.js.map